if (!customElements.get("nmn-supplement-quiz")) {
  customElements.define(
    "nmn-supplement-quiz",
    class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.form = this.querySelector("[data-quiz-form]");
        this.results = this.querySelector("[data-quiz-results]");
        this.status = this.querySelector("[data-quiz-status]");
        this.answerList = this.querySelector("[data-quiz-answers]");
        this.questionTitle = this.querySelector("[data-quiz-question]");
        this.selectionError = this.querySelector("[data-quiz-selection-error]");
        this.productList = this.querySelector("[data-quiz-products]");
        this.progress = this.querySelector("[data-quiz-progress]");
        if (!this.form || !this.answerList || !this.productList) return;
        this.answers ||= [];
        this.step ??= 0;
        window.addEventListener("pageshow", () => this.resumeState(), options);

        this.addEventListener("click", (event) => {
          const radio = event.target.closest('input[type="radio"]');
          if (radio && event.detail > 1) {
            event.preventDefault();
            return;
          }
          if (radio && event.detail > 0) {
            this.selectRadio(radio);
            this.submitAnswer();
            return;
          }
          const button = event.target.closest("button");
          if (!button) return;
          if (button.hasAttribute("data-quiz-back")) {
            if (this.step > 0) this.showQuestion(this.step - 1);
          }
          if (button.hasAttribute("data-quiz-review")) this.showQuestion(0);
          if (button.hasAttribute("data-quiz-results-back")) this.showQuestion(this.rules.questions.length - 1);
          if (button.hasAttribute("data-quiz-restart")) {
            this.answers = [];
            this.age = undefined;
            this.showQuestion(0);
          }
          if (button.hasAttribute("data-quiz-load-retry")) this.initialize();
        }, options);

        this.form.addEventListener("change", (event) => {
          if (!event.target.matches("input[type=radio]")) return;
          this.selectRadio(event.target);
        }, options);

        this.form.addEventListener("keydown", (event) => {
          if (!event.target.matches('input[type="radio"]') || ![" ", "Enter"].includes(event.key)) return;
          event.preventDefault();
          this.selectRadio(event.target);
          this.submitAnswer();
        }, options);

        this.form.addEventListener("input", (event) => {
          if (event.target.matches("[data-quiz-age-number], [data-quiz-age-range]")) {
            this.updateAge(event.target);
          }
        }, options);

        this.form.addEventListener("pointerdown", (event) => {
          if (event.target.matches("[data-quiz-age-range]")) this.updateAge(event.target);
        }, options);

        this.form.addEventListener("submit", (event) => {
          event.preventDefault();
          this.submitAnswer();
        }, options);

        this.initialization = this.initialize();
      }

      selectRadio(radio) {
        if (!Number.isInteger(this.step) || !this.form.contains(radio)) return;
        radio.checked = true;
        this.answers[this.step] = Number(radio.value);
        this.selectionError.hidden = true;
        this.answerList.removeAttribute("aria-invalid");
        this.productList.replaceChildren();
        this.updateContinue();
        this.saveState();
      }

      updateContinue() {
        this.querySelector("[data-quiz-next]").disabled = !Number.isInteger(this.answers[this.step]);
      }

      submitAnswer() {
        if (!this.rules || !Number.isInteger(this.answers[this.step])) return;
        if (this.step < this.rules.questions.length - 1) this.showQuestion(this.step + 1);
        else this.showResults();
      }

      // The legacy bands omitted age 25 and overlapped at 55. Exact ages use inclusive lower bounds.
      static ageAnswerIndex(age) {
        if (!Number.isInteger(age) || age < 18 || age > 55) return undefined;
        if (age < 26) return 0;
        if (age < 36) return 1;
        if (age < 46) return 2;
        if (age < 55) return 3;
        return 4;
      }

      static sessionState(value, rules) {
        if (!value || value.version !== 1 || value.rulesVersion !== rules.version ||
            !value.answers || typeof value.answers !== "object" || Array.isArray(value.answers)) return null;
        const age = this.ageAnswerIndex(value.age) === undefined ? undefined : value.age;
        const answers = rules.questions.map((question) => {
          const index = question.answers.findIndex((answer) => answer.id === value.answers[question.id]);
          return index === -1 ? undefined : index;
        });
        answers[0] = this.ageAnswerIndex(age);
        const firstMissing = answers.findIndex((answer) => answer === undefined);
        let step = value.step === "results" ? "results" : rules.questions.findIndex((question) => question.id === value.step);
        if (step === -1) step = 0;
        if (firstMissing !== -1 && (step === "results" || step > firstMissing)) step = firstMissing;
        return { age, answers, step };
      }

      restoreState() {
        if (!this.rules) return false;
        try {
          const raw = window.sessionStorage.getItem("nmnbio.supplement-quiz");
          if (!raw) return false;
          const state = this.constructor.sessionState(JSON.parse(raw), this.rules);
          if (!state || JSON.stringify([this.age, this.answers, this.step]) ===
              JSON.stringify([state.age, state.answers, state.step])) return false;
          this.age = state.age;
          this.answers = state.answers;
          this.step = state.step;
          return true;
        } catch (error) {
          // The quiz remains usable when browser storage is blocked or a saved record is invalid.
          return false;
        }
      }

      saveState() {
        if (!this.rules) return;
        const answers = {};
        this.rules.questions.forEach((question, index) => {
          const answer = question.answers[this.answers[index]];
          if (answer) answers[question.id] = answer.id;
        });
        try {
          window.sessionStorage.setItem("nmnbio.supplement-quiz", JSON.stringify({
            version: 1,
            rulesVersion: this.rules.version,
            age: this.age ?? null,
            answers,
            step: this.step === "results" ? "results" : this.rules.questions[this.step]?.id,
          }));
        } catch (error) {
          // Keep the current in-memory answers if session storage is unavailable.
        }
      }

      resumeState() {
        if (!this.restoreState()) return;
        if (this.step === "results") return this.showResults(false);
        this.showQuestion(this.step, false);
      }

      renderAge() {
        const container = document.createElement("div");
        container.className = "nmn-quiz__age";
        const label = document.createElement("label");
        label.className = "nmn-quiz__age-label";
        label.htmlFor = `NmnQuizAge-${this.dataset.quizId}`;
        label.textContent = this.dataset.ageLabel;
        const field = document.createElement("div");
        field.className = "nmn-quiz__age-field";
        const number = document.createElement("input");
        number.type = "number";
        number.id = label.htmlFor;
        number.inputMode = "numeric";
        number.min = "18";
        number.max = "55";
        number.step = "1";
        number.required = true;
        number.placeholder = "—";
        number.dataset.quizAgeNumber = "";
        number.value = this.age ?? "";
        number.setAttribute("aria-describedby", `NmnQuizHint-${this.dataset.quizId}`);
        const numberField = document.createElement("div");
        numberField.className = "nmn-quiz__age-number";
        const plus = document.createElement("span");
        plus.className = "nmn-quiz__age-plus";
        plus.dataset.quizAgePlus = "";
        plus.textContent = "+";
        plus.setAttribute("aria-hidden", "true");
        plus.hidden = this.age !== 55;
        numberField.append(number, plus);
        const description = document.createElement("span");
        description.id = `NmnQuizAgeTop-${this.dataset.quizId}`;
        description.className = "visually-hidden";
        description.textContent = this.dataset.ageTopHint;
        number.setAttribute("aria-describedby", `NmnQuizHint-${this.dataset.quizId} ${description.id}`);
        const suffix = document.createElement("span");
        suffix.textContent = this.dataset.ageUnit;
        field.append(numberField, suffix, description);
        const rangeLabel = document.createElement("label");
        rangeLabel.className = "visually-hidden";
        rangeLabel.htmlFor = `NmnQuizAgeRange-${this.dataset.quizId}`;
        rangeLabel.textContent = this.dataset.ageSliderLabel;
        const range = document.createElement("input");
        range.type = "range";
        range.id = rangeLabel.htmlFor;
        range.className = "nmn-quiz__age-range";
        range.min = "18";
        range.max = "55";
        range.step = "1";
        range.value = this.age ?? 40;
        range.dataset.quizAgeRange = "";
        range.setAttribute("aria-describedby", `NmnQuizHint-${this.dataset.quizId}`);
        range.setAttribute("aria-valuetext", this.age === undefined ? this.dataset.ageUnset : `${this.age === 55 ? this.dataset.ageTopLabel : this.age} ${this.dataset.ageUnit}`);
        range.style.setProperty("--quiz-age-progress", this.age === undefined ? "0%" : `${(this.age - 18) / 37 * 100}%`);
        const limits = document.createElement("div");
        limits.className = "nmn-quiz__age-limits";
        limits.setAttribute("aria-hidden", "true");
        ["18", this.dataset.ageTopLabel].forEach((value) => {
          const limit = document.createElement("span");
          limit.textContent = value;
          limits.append(limit);
        });
        container.append(label, field, rangeLabel, range, limits);
        this.answerList.replaceChildren(container);
      }

      updateAge(control) {
        const age = Number.isInteger(control.valueAsNumber) ? Math.min(control.valueAsNumber, 55) : control.valueAsNumber;
        const answerIndex = this.constructor.ageAnswerIndex(age);
        const number = this.querySelector("[data-quiz-age-number]");
        const range = this.querySelector("[data-quiz-age-range]");
        this.age = answerIndex === undefined ? undefined : age;
        this.answers[0] = answerIndex;
        number.setAttribute("aria-invalid", String(control.value !== "" && answerIndex === undefined));
        if (answerIndex !== undefined) {
          if (control === range || control.valueAsNumber > 55) number.value = age;
          else range.value = age;
        }
        this.querySelector("[data-quiz-age-plus]").hidden = this.age !== 55;
        range.setAttribute("aria-valuetext", this.age === undefined ? this.dataset.ageUnset : `${age === 55 ? this.dataset.ageTopLabel : age} ${this.dataset.ageUnit}`);
        range.style.setProperty("--quiz-age-progress", this.age === undefined ? "0%" : `${(age - 18) / 37 * 100}%`);
        this.productList.replaceChildren();
        this.updateContinue();
        this.saveState();
      }

      async initialize() {
        const controller = this.controller;
        this.querySelector("[data-quiz-load-retry]").hidden = true;
        try {
          if (!this.rules) {
            this.status.textContent = this.dataset.loading;
            const response = await fetch(this.dataset.rulesUrl, { signal: controller.signal });
            if (!response.ok) throw new Error("Quiz questions unavailable");
            const rules = await response.json();
            if (!Array.isArray(rules.questions) || rules.questions.length !== 9 ||
                rules.questions.some((question) => !question.title || !question.answers?.length)) {
              throw new Error("Invalid quiz questions");
            }
            this.rules = rules;
          }
          if (controller.signal.aborted) return;
          this.restoreState();
          this.status.textContent = "";
          this.dataset.ready = "true";
          if (this.step === "results") this.showResults(false);
          else this.showQuestion(this.step, false);
        } catch (error) {
          if (!controller.signal.aborted) {
            this.status.textContent = this.dataset.error;
            this.querySelector("[data-quiz-load-retry]").hidden = false;
          }
        }
      }

      showQuestion(index, focus = true) {
        if (!this.rules?.questions[index]) return;
        this.catalogueRequest?.abort();
        this.step = index;
        this.dataset.step = "question";
        const question = this.rules.questions[index];
        this.form.hidden = false;
        this.results.hidden = true;
        this.updateSidebar(false);
        this.status.textContent = "";
        this.status.classList.remove("visually-hidden");
        this.selectionError.hidden = true;
        this.answerList.removeAttribute("aria-invalid");
        this.querySelector("[data-quiz-back]").hidden = index === 0;
        this.questionTitle.textContent = index === 0 ? this.dataset.ageQuestion : question.title;
        this.querySelector(".nmn-quiz__hint").textContent = index === 0 ? this.dataset.ageHint : this.dataset.choiceHint;
        this.closest("dialog")?.setAttribute("aria-labelledby", this.questionTitle.id);
        this.querySelector("[data-quiz-step-label]").textContent = this.dataset.questionLabel
          .replace("[current]", index + 1).replace("[total]", this.rules.questions.length);
        this.progress.setAttribute("aria-valuenow", index + 1);
        this.progress.setAttribute("aria-valuetext", this.querySelector("[data-quiz-step-label]").textContent);
        this.progress.replaceChildren(...this.rules.questions.map((_, progressIndex) => {
          const segment = document.createElement("span");
          segment.dataset.complete = String(progressIndex <= index);
          return segment;
        }));
        this.querySelectorAll("[data-quiz-group]").forEach((group, groupIndex) => {
          group.removeAttribute("aria-current");
          if (groupIndex === question.group) group.setAttribute("aria-current", "step");
          group.dataset.complete = String(groupIndex < question.group);
          group.querySelector("[data-quiz-group-number]").hidden = groupIndex < question.group;
          group.querySelector("[data-quiz-group-check]").hidden = groupIndex >= question.group;
        });

        const isComparison = index === 2 || index === 3;
        this.answerList.classList.toggle("nmn-quiz__answers--grid", index !== 0 && question.answers.length > 4);
        this.answerList.classList.toggle("nmn-quiz__answers--comparison", isComparison);
        if (index === 0) this.renderAge();
        else this.answerList.replaceChildren(...(isComparison ? [1, 2, 0] : question.answers.map((_, answerIndex) => answerIndex)).map((answerIndex) => {
          const answer = question.answers[answerIndex];
          const label = document.createElement("label");
          label.className = "nmn-quiz__answer";
          const input = document.createElement("input");
          input.type = "radio";
          input.name = `quiz-answer-${this.dataset.quizId}`;
          input.id = `NmnQuizAnswer-${this.dataset.quizId}-${index}-${answerIndex}`;
          input.value = answerIndex;
          input.required = true;
          input.checked = this.answers[index] === answerIndex;
          input.setAttribute("aria-describedby", `NmnQuizHint-${this.dataset.quizId}`);
          label.htmlFor = input.id;
          const text = document.createElement("span");
          text.textContent = answer.label;
          if (isComparison) {
            input.className = "visually-hidden";
            const symbol = document.createElement("span");
            symbol.className = "nmn-quiz__comparison-symbol";
            symbol.setAttribute("aria-hidden", "true");
            const icon = this.querySelector(`[data-quiz-comparison-icon="${answerIndex}"]`);
            if (icon) symbol.append(icon.content.cloneNode(true));
            label.append(input, symbol, text);
          } else label.append(input, text);
          return label;
        }));

        this.querySelector("[data-quiz-next-label]").textContent = index === this.rules.questions.length - 1
          ? this.dataset.resultsLabel : this.dataset.continueLabel;
        this.updateContinue();
        this.saveState();
        if (focus) {
          this.questionTitle.focus({ preventScroll: true });
          this.questionTitle.scrollIntoView({ block: "nearest", behavior: "instant" });
        }
      }

      // The main goal determines eligibility; other answers refine ingredient relevance.
      static recommendProducts(rules, answers, products) {
        const goalIndex = rules.questions.findIndex((question) => question.id === rules.goalQuestion);
        const goal = rules.questions[goalIndex]?.answers[answers[goalIndex]];
        if (!goal) return { goal: null, singles: [], bundles: [] };
        const scores = new Map(Object.keys(rules.ingredients).map((key) => [key, 0]));
        rules.questions.forEach((question, index) => {
          const answer = question.answers[answers[index]];
          Object.entries(answer?.scores || {}).forEach(([key, amount]) => {
            if (scores.has(key)) scores.set(key, scores.get(key) + amount);
          });
        });
        const matchedIngredients = goal.eligibleIngredients.filter((key) => scores.get(key) >= rules.minimumScore);
        // Duplicate product mappings must not inflate scores or repeat a physical product.
        const uniqueProducts = new Map();
        products.forEach((product) => {
          if (!matchedIngredients.includes(product.key)) return;
          const existing = uniqueProducts.get(product.handle);
          const score = scores.get(product.key);
          if (!existing || score > existing.score) uniqueProducts.set(product.handle, { ...product, score });
        });
        const singles = Array.from(uniqueProducts.values())
          .sort((first, second) => second.score - first.score || first.order - second.order)
          .slice(0, rules.maximumProducts);
        const selectedHandles = new Set(singles.map((product) => product.handle));
        const bundleCandidates = products.flatMap((product) => {
          const ingredients = rules.bundles[product.key];
          if (!goal.eligibleBundles.includes(product.key) || !ingredients || selectedHandles.has(product.handle)) return [];
          const matches = ingredients.filter((key) => matchedIngredients.includes(key));
          // Every additional ingredient needs positive support; a large bundle gets no automatic bonus.
          if (!matches.length || ingredients.some((key) => !(scores.get(key) > 0))) return [];
          const score = matches.reduce((total, key) => total + scores.get(key), 0) / matches.length;
          return [{ ...product, ingredients, matches, score }];
        }).sort((first, second) => second.matches.length - first.matches.length ||
          second.score - first.score || first.ingredients.length - second.ingredients.length || first.order - second.order);
        const bundles = [];
        const maximumBundles = Math.min(rules.maximumBundleAlternatives, rules.maximumProducts - singles.length);
        for (const product of bundleCandidates) {
          if (bundles.length >= maximumBundles) break;
          if (selectedHandles.has(product.handle)) continue;
          selectedHandles.add(product.handle);
          bundles.push(product);
        }
        return { goal, singles, bundles };
      }

      static matchingBenefits(rules, answers, productKey) {
        const matches = new Map();
        rules.questions.forEach((question, index) => {
          const answer = question.answers[answers[index]];
          const topic = answer?.benefits?.[productKey];
          const solution = rules.productBenefits?.[productKey]?.[topic];
          if (!solution || !answer.matchLabel) return;
          const isGoal = question.id === rules.goalQuestion;
          const existing = matches.get(topic);
          // Specific symptoms replace the general goal label without repeating a benefit.
          matches.set(topic, {
            topic,
            problem: answer.matchLabel,
            solution,
            isGoal: isGoal || existing?.isGoal || false,
            order: existing?.order ?? index,
          });
        });
        return Array.from(matches.values())
          .sort((first, second) => Number(second.isGoal) - Number(first.isGoal) || first.order - second.order)
          .slice(0, rules.maximumBenefits);
      }

      static summariseAnswers(rules, answers) {
        const summaries = new Map();
        rules.questions.forEach((question, index) => {
          const summary = question.answers[answers[index]]?.summary;
          if (!summary?.topic || !summary.text) return;
          const existing = summaries.get(summary.topic);
          summaries.set(summary.topic, {
            text: summary.text,
            isGoal: question.id === rules.goalQuestion || existing?.isGoal || false,
            order: existing?.order ?? index,
          });
        });
        return Array.from(summaries.values())
          .sort((first, second) => Number(second.isGoal) - Number(first.isGoal) || first.order - second.order)
          .map((summary) => summary.text);
      }

      updateSidebar(isResults) {
        const progress = this.querySelector("[data-quiz-sidebar-progress]");
        const results = this.querySelector("[data-quiz-sidebar-results]");
        const disclaimer = this.querySelector("[data-quiz-sidebar-disclaimer]");
        if (progress) progress.hidden = isResults;
        if (results) results.hidden = !isResults;
        if (disclaimer) disclaimer.hidden = !isResults;
        const aside = this.querySelector(".nmn-quiz__aside");
        if (isResults) aside?.setAttribute("aria-labelledby", `NmnQuizResults-${this.dataset.quizId}`);
        else aside?.removeAttribute("aria-labelledby");
        const needs = this.querySelector("[data-quiz-needs]");
        if (isResults && needs) {
          needs.replaceChildren(...this.constructor.summariseAnswers(this.rules, this.answers).map((summary) => {
            const item = document.createElement("li");
            item.textContent = summary;
            return item;
          }));
        }
      }

      renderResults(picks) {
        const nodes = [];
        const createGroup = (label, products, isBundle) => {
          const group = document.createElement(isBundle ? "section" : "div");
          group.className = "nmn-quiz__product-group";
          if (isBundle) {
            const heading = document.createElement("h3");
            heading.className = "nmn-quiz__option-heading";
            heading.id = `NmnQuizOption-${this.dataset.quizId}-bundle`;
            heading.textContent = label;
            group.setAttribute("aria-labelledby", heading.id);
            group.append(heading);
          }
          products.forEach((product) => {
            const copy = product.card.querySelector(".nmn-quiz__product-copy");
            const title = document.createElement(isBundle ? "h4" : "h3");
            const previousTitle = copy.querySelector("h3, h4");
            title.textContent = previousTitle.textContent;
            previousTitle.replaceWith(title);
            // Re-rendering the same cards must not accumulate duplicate reasons or contents.
            copy.querySelectorAll("[data-quiz-benefits], [data-quiz-bundle-contents]").forEach((element) => element.remove());
            const benefits = this.constructor.matchingBenefits(this.rules, this.answers, product.key);
            if (benefits.length) {
              const list = document.createElement("ul");
              list.className = "nmn-quiz__benefits";
              list.dataset.quizBenefits = "";
              benefits.forEach((benefit) => {
                const item = document.createElement("li");
                const check = document.createElement("span");
                check.className = "nmn-quiz__benefit-check";
                check.setAttribute("aria-hidden", "true");
                const icon = this.querySelector("[data-quiz-benefit-icon]");
                if (icon) check.append(icon.content.cloneNode(true));
                const text = document.createElement("span");
                const problem = document.createElement("strong");
                problem.textContent = `${benefit.problem}: `;
                text.append(problem, document.createTextNode(benefit.solution));
                item.append(check, text);
                list.append(item);
              });
              copy.querySelector(".nmn-link").before(list);
            }
            if (isBundle) {
              const contents = document.createElement("p");
              contents.className = "nmn-quiz__bundle-contents";
              contents.dataset.quizBundleContents = "";
              contents.textContent = this.dataset.bundleContents.replace("[ingredients]",
                product.ingredients.map((key) => this.rules.ingredients[key].label).join(" + "));
              title.after(contents);
            }
            group.append(product.card);
          });
          return group;
        };
        if (picks.singles.length) nodes.push(createGroup(null, picks.singles, false));
        if (picks.bundles.length) nodes.push(createGroup(this.dataset.bundleLabel, picks.bundles, true));
        this.productList.replaceChildren(...nodes);
      }

      async showResults(focus = true) {
        if (!this.rules) return;
        const missingAnswer = this.rules.questions.findIndex((question, index) => !question.answers[this.answers[index]]);
        if (missingAnswer !== -1) {
          this.showQuestion(missingAnswer);
          return;
        }
        this.catalogueRequest?.abort();
        const request = new AbortController();
        this.catalogueRequest = request;
        this.step = "results";
        this.dataset.step = "results";
        this.saveState();
        this.form.hidden = true;
        this.results.hidden = false;
        this.updateSidebar(true);
        this.closest("dialog")?.setAttribute("aria-labelledby", `NmnQuizResults-${this.dataset.quizId}`);
        this.productList.replaceChildren();
        this.productList.setAttribute("aria-busy", "true");
        this.querySelector("[data-quiz-empty]").hidden = true;
        this.status.textContent = this.dataset.loading;
        this.status.classList.remove("visually-hidden");
        this.querySelectorAll("[data-quiz-group]").forEach((group) => {
          group.removeAttribute("aria-current");
          group.dataset.complete = "true";
          group.querySelector("[data-quiz-group-number]").hidden = true;
          group.querySelector("[data-quiz-group-check]").hidden = false;
        });
        if (focus) {
          const heading = this.querySelector("[data-quiz-results-title]");
          heading.focus({ preventScroll: true });
          heading.scrollIntoView({ block: "nearest", behavior: "instant" });
        }
        try {
          let url = new URL(this.dataset.productsUrl, window.location.origin);
          url.searchParams.set("sort_by", "best-selling");
          url.searchParams.set("section_id", "supplement-quiz-products");
          const handles = [];
          let cards;
          const visitedPages = new Set();
          while (url) {
            if (url.origin !== window.location.origin || visitedPages.has(url.href)) throw new Error("Invalid catalogue page");
            visitedPages.add(url.href);
            const response = await fetch(url, { signal: request.signal });
            if (!response.ok) throw new Error("Quiz catalogue unavailable");
            const markup = new DOMParser().parseFromString(await response.text(), "text/html");
            const orderData = markup.querySelector("[data-quiz-product-order]");
            const catalogue = markup.querySelector("[data-quiz-catalogue]");
            if (!orderData || !catalogue) throw new Error("Missing quiz catalogue");
            cards ||= Array.from(catalogue.querySelectorAll("[data-quiz-product]"));
            const order = JSON.parse(orderData.textContent);
            if (!Array.isArray(order.handles)) throw new Error("Missing best-selling order");
            handles.push(...order.handles);
            if (!order.next || cards.every((card) => handles.includes(card.dataset.handle))) break;
            url = new URL(order.next, window.location.origin);
            url.searchParams.set("sort_by", "best-selling");
            url.searchParams.set("section_id", "supplement-quiz-products");
          }
          if (request.signal.aborted || !this.isConnected) return;
          const products = cards.map((card) => ({
            key: card.dataset.key,
            handle: card.dataset.handle,
            order: handles.indexOf(card.dataset.handle),
            card,
          })).filter((product) => product.order >= 0);
          const picks = this.constructor.recommendProducts(this.rules, this.answers, products);
          this.renderResults(picks);
          this.querySelector("[data-quiz-empty]").hidden = picks.singles.length + picks.bundles.length > 0;
          this.status.textContent = this.dataset.completeLabel;
          this.status.classList.add("visually-hidden");
        } catch (error) {
          if (!request.signal.aborted) {
            this.status.textContent = this.dataset.error;
          }
        } finally {
          if (this.catalogueRequest === request) this.productList.removeAttribute("aria-busy");
        }
      }

      disconnectedCallback() {
        this.catalogueRequest?.abort();
        this.controller?.abort();
      }
    },
  );
}
